import React, { useRef, useState } from "react";
import {
  Paper,
  Stack,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Popover,
  useTheme,
  Typography,
  InputAdornment,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { CalendarDots, UsersThree } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";

import { ModuleContainer } from "../styles";
import ModuleHeader from "../ModuleHeader";
import EmptyCaseloadSummary from "./EmptyCaseloadSummary";
import DateRangePickerErrorBoundary from "components/DateRangePickerErrorBoundary";
import { formatDateRange } from "../utils";

export const TIME_RANGES = {
  THIS_WEEK: "this_week",
  LAST_WEEK: "last_week",
  CUSTOM: "custom",
} as const;

export const STATUS_OPTIONS = {
  ALL: "all",
  ACTIVE: "active",
  DISCHARGING: "discharging",
} as const;

export const DISPLAY_UNITS = {
  HOURS: "hours",
  MINUTES: "minutes",
} as const;

type TimeRange = (typeof TIME_RANGES)[keyof typeof TIME_RANGES];
type StatusOption = (typeof STATUS_OPTIONS)[keyof typeof STATUS_OPTIONS];
type DisplayUnit = (typeof DISPLAY_UNITS)[keyof typeof DISPLAY_UNITS];

interface CaseloadSummaryRow {
  client_id: number;
  client_name: string;
  status: "Active" | "Discharging";
  authorized_hours: number;
  scheduled_hours: number;
  delivered_hours: number;
  discharge_date: string | null;
}

interface CaseloadSummaryModuleProps {
  data: CaseloadSummaryRow[] | null;
  timeRange: TimeRange;
  onTimeRangeChange: (value: TimeRange) => void;
  statusFilter: StatusOption;
  onStatusFilterChange: (value: StatusOption) => void;
  displayUnit: DisplayUnit;
  onDisplayUnitChange: (value: DisplayUnit) => void;
  isSmallScreen: boolean;
  customDateRange: [Date | null, Date | null];
  onCustomDateRangeChange: (value: [Date | null, Date | null]) => void;
}

const formatHoursValue = (value: number | null | undefined, displayUnit: DisplayUnit): string => {
  if (value === null || value === undefined)
    return displayUnit === DISPLAY_UNITS.HOURS ? "0h" : "0m";
  if (displayUnit === DISPLAY_UNITS.MINUTES) {
    return `${Math.round(value * 60)}m`;
  }
  return `${Math.round(value)}h`;
};

const getTimeRangeLabel = (timeRange: TimeRange): string => {
  if (timeRange === TIME_RANGES.CUSTOM) return "Custom range";
  if (timeRange === TIME_RANGES.LAST_WEEK) return "Last week";
  return "This week";
};

const CaseloadSummaryModule: React.FC<CaseloadSummaryModuleProps> = ({
  data,
  timeRange,
  onTimeRangeChange,
  statusFilter,
  onStatusFilterChange,
  displayUnit,
  onDisplayUnitChange,
  isSmallScreen,
  customDateRange,
  onCustomDateRangeChange,
}) => {
  const isEmpty = !data || data.length === 0;
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const timeRangeRef = useRef<HTMLDivElement>(null);
  const { palette } = useTheme();

  const handleTimeRangeChange = (event: SelectChangeEvent<TimeRange>) => {
    const value = event.target.value as TimeRange;
    onTimeRangeChange(value);
  };

  const handleDateRangeChange = (newValue: [Date | null, Date | null]) => {
    onCustomDateRangeChange(newValue);
    if (newValue[0] && newValue[1]) {
      setDatePickerOpen(false);
    }
  };

  return (
    <Paper
      component="section"
      elevation={2}
      sx={ModuleContainer}
      data-testid="caseload-summary-module"
    >
      <Stack
        direction={isSmallScreen ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isSmallScreen ? "flex-start" : "center"}
        mb={7}
        gap={isSmallScreen ? 4 : 0}
      >
        <ModuleHeader
          Icon={UsersThree}
          title="Caseload Summary"
          subtitle={
            isEmpty ? null : (
              <>
                Number of clients in the summary:{" "}
                <Typography component="span" color="primary.main" variant="subtitle2">
                  {data.length}
                </Typography>{" "}
              </>
            )
          }
        />

        <Stack direction="row" gap={3} sx={{ width: isSmallScreen ? "100%" : "auto" }}>
          <Stack sx={{ width: isSmallScreen ? "100%" : "auto" }}>
            <Select
              value={timeRange}
              onChange={handleTimeRangeChange}
              renderValue={() => getTimeRangeLabel(timeRange)}
              data-testid="caseload-time-range-select"
            >
              <MenuItem value={TIME_RANGES.THIS_WEEK}>This week</MenuItem>
              <MenuItem value={TIME_RANGES.LAST_WEEK}>Last week</MenuItem>
              <MenuItem value={TIME_RANGES.CUSTOM}>Custom range</MenuItem>
            </Select>
          </Stack>

          <div
            ref={timeRangeRef}
            id="caseload-summary-datepicker"
            style={{
              display: timeRange === TIME_RANGES.CUSTOM ? undefined : "none",
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            <TextField
              value={formatDateRange(customDateRange)}
              placeholder="Select date range"
              onClick={(event) => {
                // currentTarget here is TextField's root element, not the
                // input that actually holds focus.
                event.currentTarget.querySelector("input")?.blur();
                setDatePickerOpen(true);
              }}
              sx={{ width: isSmallScreen ? "100%" : "auto" }}
              inputProps={{ "aria-label": "Date range", readOnly: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarDots size={20} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Popover
            open={datePickerOpen}
            anchorEl={timeRangeRef.current}
            onClose={() => setDatePickerOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <DateRangePickerErrorBoundary>
              <StaticDateRangePicker
                value={customDateRange}
                onChange={handleDateRangeChange as any}
                calendars={2}
                slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
              />
            </DateRangePickerErrorBoundary>
          </Popover>

          <Select
            value={statusFilter}
            fullWidth={isSmallScreen}
            onChange={(e) => onStatusFilterChange(e.target.value as StatusOption)}
            data-testid="caseload-status-select"
          >
            <MenuItem value={STATUS_OPTIONS.ALL}>All</MenuItem>
            <MenuItem value={STATUS_OPTIONS.ACTIVE}>Active</MenuItem>
            <MenuItem value={STATUS_OPTIONS.DISCHARGING}>Discharging</MenuItem>
          </Select>

          <Select
            value={displayUnit}
            fullWidth={isSmallScreen}
            onChange={(e) => onDisplayUnitChange(e.target.value as DisplayUnit)}
            data-testid="caseload-unit-select"
          >
            <MenuItem value={DISPLAY_UNITS.HOURS}>Hours</MenuItem>
            <MenuItem value={DISPLAY_UNITS.MINUTES}>Minutes</MenuItem>
          </Select>
        </Stack>
      </Stack>

      {isEmpty ? (
        <EmptyCaseloadSummary />
      ) : (
        <TableContainer sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <Table data-testid="caseload-summary-table">
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Authorized</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Discharge Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.client_id} data-testid={`caseload-row-${row.client_id}`} hover>
                  <TableCell>
                    <Typography
                      component="a"
                      href={`/clients/${row.client_id}/dashboard`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#8E42CA",
                        fontSize: "14px",
                        fontWeight: 400,
                        cursor: "pointer",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                        "&:focus-visible": {
                          textDecoration: "underline",
                          outline: "2px solid #8E42CA",
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      {row.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === "Active" ? "success" : "warning"}
                    />
                  </TableCell>
                  <TableCell>{formatHoursValue(row.authorized_hours, displayUnit)}</TableCell>
                  <TableCell>{formatHoursValue(row.scheduled_hours, displayUnit)}</TableCell>
                  <TableCell
                    sx={{
                      color: row.delivered_hours === 0 ? palette.error.main : "inherit",
                      fontWeight: row.delivered_hours === 0 ? 600 : 400,
                    }}
                  >
                    {formatHoursValue(row.delivered_hours, displayUnit)}
                  </TableCell>
                  <TableCell>
                    {row.discharge_date && row.status === "Discharging"
                      ? format(parseISO(row.discharge_date.slice(0, 10)), "MM/dd/yy")
                      : "\u2014"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default CaseloadSummaryModule;
